/**
 * FormBuilder Component
 *
 * Main form builder component that orchestrates the sidebar, canvas, toolbar,
 * and settings panels. Provides a complete drag-and-drop form building experience.
 */

import { forwardRef, memo, useEffect, useCallback } from 'react';
import { Box } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import {
  useFormBuilderStore,
  selectForm,
  selectShowPreview,
  selectSelectedFieldId,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import FormBuilderToolbar from './FormBuilderToolbar';
import FormBuilderSidebar from './FormBuilderSidebar';
import FormBuilderCanvas from './FormBuilderCanvas';
import FormBuilderPreview from './FormBuilderPreview';
import FieldSettingsPanel from './FieldSettingsPanel';
import FormSettingsPanel from './FormSettingsPanel';
import type { FormBuilderProps } from './types';
import classes from './FormBuilder.module.css';

/**
 * FormBuilder Component
 *
 * A comprehensive drag-and-drop form builder that allows users to:
 * - Add fields from a palette by dragging or clicking
 * - Reorder fields within the form
 * - Configure field properties, validation, and conditional logic
 * - Preview the form in different viewport sizes
 * - Export/import form definitions as JSON
 *
 * @example
 * // Create a new form
 * <FormBuilder
 *   onSave={(form) => saveFormToDatabase(form)}
 *   onCancel={() => navigate('/forms')}
 * />
 *
 * @example
 * // Edit an existing form
 * <FormBuilder
 *   initialForm={existingForm}
 *   onSave={(form) => updateFormInDatabase(form)}
 * />
 *
 * @example
 * // Read-only view
 * <FormBuilder
 *   initialForm={formToView}
 *   readOnly
 * />
 */
const FormBuilder = forwardRef<HTMLDivElement, FormBuilderProps>(
  ({ initialForm, onSave, onCancel, readOnly = false, className }, ref) => {
    const form = useFormBuilderStore(selectForm);
    const showPreview = useFormBuilderStore(selectShowPreview);
    const selectedFieldId = useFormBuilderStore(selectSelectedFieldId);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const {
      setForm,
      createNewForm,
      setEditMode,
      undo,
      redo,
      removeSelectedFields,
      copySelectedFields,
      pasteFields,
      duplicateField,
      selectAllFields,
      clearSelection,
      exportFormForRender,
    } = useFormBuilderStore();

    // Initialize form on mount
    useEffect(() => {
      if (initialForm) {
        setForm(initialForm);
      } else if (!form) {
        createNewForm();
      }

      // Set edit mode based on readOnly prop
      setEditMode(!readOnly);
    }, [initialForm, form, readOnly, setForm, createNewForm, setEditMode]);

    // Handle save callback
    const handleSave = useCallback(() => {
      const formData = exportFormForRender();
      if (formData && onSave) {
        onSave(formData);
      }
    }, [exportFormForRender, onSave]);

    // Keyboard shortcuts
    useHotkeys([
      // Undo/Redo
      ['mod+z', () => !readOnly && undo()],
      ['mod+y', () => !readOnly && redo()],
      ['mod+shift+z', () => !readOnly && redo()],

      // Delete selected
      ['Delete', () => !readOnly && removeSelectedFields()],
      ['Backspace', () => !readOnly && removeSelectedFields()],

      // Copy/Paste
      ['mod+c', () => copySelectedFields()],
      ['mod+v', () => !readOnly && pasteFields()],

      // Duplicate
      [
        'mod+d',
        (e) => {
          e.preventDefault();
          if (!readOnly && selectedFieldId) {
            duplicateField(selectedFieldId);
          }
        },
      ],

      // Select all
      [
        'mod+a',
        (e) => {
          e.preventDefault();
          selectAllFields();
        },
      ],

      // Escape to clear selection
      ['Escape', () => clearSelection()],

      // Save
      [
        'mod+s',
        (e) => {
          e.preventDefault();
          if (!readOnly) {
            handleSave();
          }
        },
      ],
    ]);

    if (!form) {
      return null;
    }

    return (
      <Box ref={ref} className={`${classes.formBuilder} ${className || ''}`}>
        {/* Toolbar */}
        <FormBuilderToolbar
          onSave={onSave ? handleSave : undefined}
          onCancel={onCancel}
        />

        {/* Main content area */}
        <Box className={classes.formBuilderContent}>
          {showPreview ? (
            // Preview mode
            <FormBuilderPreview />
          ) : (
            // Edit mode
            <>
              <FormBuilderSidebar />
              <FormBuilderCanvas />
            </>
          )}
        </Box>

        {/* Settings modals */}
        <FieldSettingsPanel />
        <FormSettingsPanel />
      </Box>
    );
  }
);

FormBuilder.displayName = 'FormBuilder';

export default memo(FormBuilder);
